import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import { HistoryResponse, tagsDetail } from "../Common/commonTypes"

export type SitesObj = {
    added_by: string
    address: string
    city: string
    city_id: number
    created_at: string
    id: number
    is_active: number
    latitude: number
    longitude: number
    name: string
    pincode: number
    site_id: string
    site_type: string
    state: string
    state_id: number
    updated_at: null
    linked_projects: any
    total_linked_count: number
    country_id: number
    csms_station_id: number
    tags?: {
        others: tagsDetail[]
        primary: tagsDetail[]
    }
}

export type SiteListPayload = {
    page: number
    per_page: number
    site?: string // Search term for site name
    pincode?: string
    added_by?: number[] | string
    state?: number[] | string
    city?: number[] | string
    status?: number | ""
    site_type?: string
    updated_at?: boolean
    created_at?: any
    showOnlyActive?: boolean
    country_id?: number
    tag_ids?: number[] | string
    download?: boolean
}

export type SiteChargerObj = {
    charger_id?: number
    charger_name: string
    charger_type: string
    is_demolished?: 0 | 1
    oem: string | null
    serial_cin: string | null
    sku_code: string | null
    operational_status: string | null
}

const MODULE_URL = `sites`

const SiteService = api.injectEndpoints({
    endpoints: build => ({
        getAllSites: build.query<{ data: SitesObj[]; totalPages: number }, SiteListPayload>({
            query: ({ showOnlyActive, ...params }) => ({
                url: `${MODULE_URL}/site_operations`,
                params: {
                    ...params,
                    updated_at: params?.updated_at ?? true,
                    country_id: params?.country_id ?? "",
                },
            }),
            transformResponse: (
                response: { data: SitesObj[]; totalPages: number },
                meta: any,
                args: SiteListPayload
            ) => {
                if (args?.showOnlyActive === false) {
                    return response
                }
                return {
                    data: response.data.filter((site: SitesObj) => site.is_active === 1),
                    totalPages: response.totalPages,
                }
            },
            providesTags: ["Sites"],
        }),
        getSiteTicketHistory: build.query<
            { data: HistoryResponse[]; totalPages: number },
            { site_id: string; page: number; per_page: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/history`,
                params: params,
            }),
            providesTags: (result, error, arg) => [{ type: "Sites", id: arg.site_id }],
        }),
        getAddedByForSites: build.query<{ id: number; user_name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/list_added_by`,
            }),
            providesTags: ["Sites"],
        }),
        getChargersBySiteId: build.query<SiteChargerObj[], { site_id: string }>({
            query: ({ site_id }) => ({
                url: `chargers_by_site_id`,
                params: {
                    site_id,
                },
            }),
            providesTags: ["Sites"],
        }),
        getSiteItems: build.query<any, { site_id: string; delivered_filter?: boolean }>({
            query: ({ site_id, delivered_filter = false }) => ({
                url: `${MODULE_URL}/site_items`,
                params: {
                    site_id,
                    delivered_filter,
                },
            }),
            providesTags: ["Sites", "Demolition"],
        }),

        // Mutations
        addSiteTicket: build.mutation<{ msg: string }, any>({
            query: body => ({
                url: `${MODULE_URL}/site_operations`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.msg }))
                }
            },
            invalidatesTags: ["Sites"],
        }),
        editSiteTicket: build.mutation<{ msg: string }, any>({
            query: body => ({
                url: `${MODULE_URL}/site_operations`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.msg }))
                }
            },
            invalidatesTags: ["Sites"],
        }),
        editSiteStatus: build.mutation<{ msg: string }, { site_id: number; status: 0 | 1 }>({
            query: body => ({
                url: `${MODULE_URL}/site_operations`,
                method: "DELETE",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Sites"],
        }),
    }),
})

export const {
    useLazyGetAllSitesQuery,
    useGetAddedByForSitesQuery,
    useGetChargersBySiteIdQuery,
    useLazyGetSiteTicketHistoryQuery,
    useGetSiteItemsQuery,
    useAddSiteTicketMutation,
    useEditSiteTicketMutation,
    useEditSiteStatusMutation,
} = SiteService

export { SiteService }
